import styled from 'styled-components'
import { Link } from 'react-router-dom'

import { Typography } from 'components/Typography/Typography'
import { VaultDisplayInfo, recommendedVaults } from 'utils/vaultDisplayInfo'
import { round } from '@kunalabs-io/kai'
import { useVaultInfo } from '@/shared/kai/hooks/useVaultInfo'
import { Carousel } from 'components/Carousel/Carousel'
import { ColumnContentLoading } from 'modules/Vaults/components/VaultRow'
import { ROUTES } from 'utils/route-constants'
import { PairInfo, RECOMMENDED_PAIR_INFOS } from 'utils/pair-info'

const VaultCard = styled(Link)`
  display: block;
  text-decoration: none;
  cursor: pointer;
  padding: 32px;
  text-align: center;
  gap: 8px;
  border-radius: 32px;
  border: 0.7px solid ${p => p.theme.colors.text.primary};
  background-color: ${p => p.theme.colors.background.primary};
  &:hover {
    // border: none;
    background-color: ${p => p.theme.colors.background.active};
  }
  width: 215px;
  height: 161px;
  flex-shrink: 0;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin: auto;
  }
`

const RecommendedVaultLabel = styled(Typography)`
  color: ${p => p.theme.colors.text.active};
  border: 0.7px solid ${p => p.theme.colors.text.active};
  padding: 6px;
  border-radius: 100px;
`

const RecommendedVaultsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  div:nth-child(2) {
    color: ${p => p.theme.colors.text.description};
  }
  div:first-child {
    margin-bottom: 32px;
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    justify-content: center;
    div:first-child {
      font-size: ${p => p.theme.typography.size.Subtitle2}px;
      text-align: center;
      margin-bottom: 16px;
    }
    div:nth-child(2) {
      font-size: ${p => p.theme.typography.size.Caption1}px;
      text-align: center !important;
      margin: 0 auto;
      max-width: 200px !important;
    }
  }
`

const RecommendedVaultCardContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 100px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const Wrapper = styled.div`
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    max-width: 1200px;
    width: 100%;
    margin: auto;
  }
`

interface Props {
  recommendedVault: PairInfo<string> | VaultDisplayInfo
}

interface SingleAssetRecommendedVaultProps {
  recommendedVault: VaultDisplayInfo
}

interface LpAssetRecommendedVaultProps {
  recommendedVault: PairInfo<string>
}

const getRecommendedVaultKey = (recommendedVault: VaultDisplayInfo | PairInfo<string>) => {
  let key = ''
  if ((recommendedVault as VaultDisplayInfo)?.vault) {
    key = (recommendedVault as VaultDisplayInfo).tSymbol
  } else {
    key = `${(recommendedVault as PairInfo<string>).coinX.displaySymbol}-${(recommendedVault as PairInfo<string>).coinY.displaySymbol}`
  }

  return key
}
export const RecommendedVaults = () => {
  const recommendedVaultsForDisplay = [...recommendedVaults, ...RECOMMENDED_PAIR_INFOS]
  return (
    <div>
      <Wrapper>
        <RecommendedVaultsContainer>
          <Typography variant="title2">
            Recommended <span style={{ fontWeight: 600 }}>Vaults</span>
          </Typography>
          <Typography variant="label2" fontWeight="light" style={{ maxWidth: 300, textAlign: 'right' }}>
            Explore Kai Finance&apos;s <span style={{ fontWeight: 600 }}>top vault picks for secure</span> DeFi yields.
          </Typography>
        </RecommendedVaultsContainer>
        <RecommendedVaultCardContainer>
          {recommendedVaultsForDisplay.map(recommendedVault => (
            <RecommendedVault recommendedVault={recommendedVault} key={getRecommendedVaultKey(recommendedVault)} />
          ))}
        </RecommendedVaultCardContainer>
        <Carousel
          data={recommendedVaultsForDisplay}
          renderItem={(item: PairInfo<string> | VaultDisplayInfo) => (
            <RecommendedVault recommendedVault={item} key={getRecommendedVaultKey(item)} />
          )}
        />
      </Wrapper>
    </div>
  )
}

const SingleAssetRecommendedVault = ({ recommendedVault }: SingleAssetRecommendedVaultProps) => {
  const vaultInfo = useVaultInfo(recommendedVault.vault, { refetchInterval: 5000 })
  const estApy = vaultInfo.apy !== undefined ? round(vaultInfo.apy * 100, 2) + '%' : ''

  return (
    <VaultCard key={recommendedVault.tSymbol} to={`/${ROUTES.VAULTS}/${recommendedVault.tSymbol}`}>
      <recommendedVault.icon />
      <Typography variant="label1">{recommendedVault.tSymbol}</Typography>
      <Typography variant="caption" color="secondary" style={{ marginBottom: 8 }}>
        Single Asset Vault
      </Typography>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <RecommendedVaultLabel variant="caption2" style={{ marginRight: 8 }}>
          Passive income
        </RecommendedVaultLabel>
        {/* <RecommendedVaultLabel variant="caption2">Exclusive KAI</RecommendedVaultLabel> */}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 24 }}>
        <Typography variant="body">APY</Typography>
        <Typography variant="body" fontWeight="bold">
          {estApy ? estApy : <ColumnContentLoading />}
        </Typography>
      </div>
    </VaultCard>
  )
}

const LpRecommendedVault = ({ recommendedVault }: LpAssetRecommendedVaultProps) => {
  return (
    <VaultCard
      key={`${recommendedVault.coinX.displaySymbol}-${recommendedVault.coinY.displaySymbol}`}
      to={`/${ROUTES.LP_VAULTS}/${recommendedVault.coinX.displaySymbol}-${recommendedVault.coinY.displaySymbol}`}
    >
      <img src={recommendedVault.coinX.iconUrl} width={32} height={32} />
      <img src={recommendedVault.coinY.iconUrl} width={32} height={32} />
      <Typography variant="label1">{`${recommendedVault.coinX.displaySymbol}-${recommendedVault.coinY.displaySymbol}`}</Typography>
      <Typography variant="caption" color="secondary" style={{ marginBottom: 8 }}>
        LP Vault
      </Typography>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <RecommendedVaultLabel variant="caption2" style={{ marginRight: 8 }}>
          Self-Managed
        </RecommendedVaultLabel>
        {/* <RecommendedVaultLabel variant="caption2">Exclusive KAI</RecommendedVaultLabel> */}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 24 }}>
        <Typography variant="body">APR</Typography>
        <Typography variant="body" fontWeight="bold">
          {recommendedVault.initialAprValue}
        </Typography>
      </div>
    </VaultCard>
  )
}

export const RecommendedVault = ({ recommendedVault }: Props) => {
  if ((recommendedVault as VaultDisplayInfo)?.vault) {
    return <SingleAssetRecommendedVault recommendedVault={recommendedVault as VaultDisplayInfo} />
  }

  return <LpRecommendedVault recommendedVault={recommendedVault as PairInfo<string>} />
}
